import { isInRange } from './his-number.utils';
import { BiPredicate, EmptyArray, NonEmptyArray } from './his-type.utils';
import { CompareBy, requireTrue, requireValue, ValueError } from './his-value.utils';

/**
 * Extends `Array.isArray`\
 * Checks if value is an array and has no elements.
 * @returns
 * - `true` only for `[]`
 * - `false` for every other value
 */
export function isEmptyArray<TArr extends readonly unknown[]>( value: TArr ): value is EmptyArray<TArr>;
export function isEmptyArray( value: unknown ): value is readonly [];
export function isEmptyArray( value: unknown ): boolean {
    return Array.isArray( value ) && value.length === 0;
}

/**
 * Extends `Array.isArray`\
 * Checks if value is an array and has at least one element.
 * The actual value of the element(s) is not relevant.
 * 
 * Allows for an explicit type cast, however the actual type is not verified by this method!
 * @returns
 * - `true` for `[1]`, `['']`, `[null]`, `[{}]`, etc.
 * - `false` for every other value
 */
export function isNonEmptyArray<TArr extends readonly unknown[]>( value: TArr ): value is NonEmptyArray<TArr>;
export function isNonEmptyArray( value: unknown ): value is readonly [unknown, ...unknown[]];
export function isNonEmptyArray( value: unknown ): boolean {
    return Array.isArray( value ) && value.length > 0;
}

/**
 * Ensures that the provided argument is an array.\
 * Throws if the argument is not an array.
 * @param arg The value to check.
 * @param propName The name of the property being checked, used in the error message.
 */
export function requireArray<T>( arg: T[], propName?: string ): ( unknown extends T ? unknown[] : T[] ) | never;
export function requireArray( arg: unknown, propName?: string ): unknown[] | never;
export function requireArray( arg: unknown, propName = 'arg' ): unknown[] {
    return requireValue( arg, Array.isArray, `${propName} must be an array` );
}

/**
 * Checks if `values` is a subset of `collection`, meaning all elements of `values` are present in `collection`.\
 * If a compare function is provided, it checks for matches based on that function instead of strict equality (`===`).
 * @param collection The array to check within.
 * @param values The array of elements to check for.
 * @param compareFn Optional function to compare elements.
 * @returns `true` if all elements of `values` are found in `collection`, `false` otherwise.
 */
export function containsAll( collection: any[], values: any[], compareFn?: BiPredicate ): boolean {
    return requireArray( values, 'values' ).every( matchesAny( requireArray( collection, 'collection' ), compareFn ) );
}

/**
 * Checks if `arrA` contains at least one element from `arrB`.\
 * If a compare function is provided, it checks for matches based on that function instead of strict equality (`===`).
 * @param arrA The array to check within.
 * @param arrB The array of elements to check for.
 * @param compareFn Optional function to compare elements.
 * @returns `true` if at least one element of `arrA` is found in `arrB`, `false` otherwise.
 */
export function containsAny( arrA: any[], arrB: any[], compareFn?: BiPredicate ): boolean {
    return requireArray( arrA, 'arrA' ).some( matchesAny( requireArray( arrB, 'arrB' ), compareFn ) );
}

/**
 * Returns the elements that are in `arrA` but not in `arrB`.\
 * If a compare function is provided, it checks for matches based on that function instead of strict equality (`===`).
 * @param arrA The array to check within.
 * @param arrB The array of elements to check for.
 * @param compareFn Optional function to compare elements.
 * @returns An array of elements that are exclusively in `arrA` and not in `arrB`.
 * @devnote Unlike `lodash.difference` which operates on a pure Set semantic (where duplicates are removed), this method considers the cardinality of elements and retains duplicates accordingly. (Comparable to Apache Commons CollectionUtils)
 */
export function difference<T = unknown>( arrA: T[], arrB: T[], compareFn?: BiPredicate<T> ): T[] {
    requireArray( arrA, 'arrA' );
    requireArray( arrB, 'arrB' );

    if ( compareFn ) {
        const remaining = [...arrB];
        return arrA.filter( consumeMatch( remaining, compareFn, 'keepUnmatched' ) );
    }

    // Fast path: use cardinality map to track how many of each element to "consume"; keep non-matches, exclude matches
    return filterByCardinality( arrA, arrB, false );
}

/**
 * Returns the elements that are both in `arrA` and `arrB`.\
 * If a compare function is provided, it checks for matches based on that function instead of strict equality (`===`).
 * @param arrA The array to check within.
 * @param arrB The array of elements to check for.
 * @param compareFn Optional function to compare elements.
 * @returns An array of elements that are present in both `arrA` and `arrB`.
 * @devnote Unlike `lodash.intersection` which operates on a pure Set semantic (where duplicates are removed), this method considers the cardinality of elements and retains duplicates accordingly. (Comparable to Apache Commons CollectionUtils)
 */
export function intersection<T = unknown>( arrA: T[], arrB: T[], compareFn?: BiPredicate<T> ): T[] {
    requireArray( arrA, 'arrA' );
    requireArray( arrB, 'arrB' );

    if ( compareFn ) {
        const remaining = [...arrB];
        return arrA.filter( consumeMatch( remaining, compareFn ) );
    }

    // Fast path: use cardinality map to track available matches; keep matches, exclude non-matches
    return filterByCardinality( arrA, arrB, true );
}

/**
 * Returns the combinations of `arrA` and `arrB`, excluding duplicates from `arrA` which are also in `arrB`.\
 * If a compare function is provided, it checks for matches based on that function instead of strict equality (`===`).
 * @param arrA The first array.
 * @param arrB The second array.
 * @param compareFn Optional function to compare elements.
 * @returns An array containing all elements from `arrB` and elements exclusive to `arrA` that are not in `arrB`.
 * @devnote Unlike `lodash.union` which operates on a pure Set semantic (where duplicates are removed), this method considers the cardinality of elements and retains duplicates accordingly. (Comparable to Apache Commons CollectionUtils)
 */
export function union<T = unknown>( arrA: T[], arrB: T[], compareFn?: BiPredicate<T> ): T[] {
    requireArray( arrA, 'arrA' );
    requireArray( arrB, 'arrB' );
    return difference( arrA, arrB, compareFn ).concat( arrB );
}

/**
 * Returns the elements that are in either `arrA` or `arrB` but not in both.\
 * If a compare function is provided, it checks for matches based on that function instead of strict equality (`===`).
 * @param arrA The first array.
 * @param arrB The second array.
 * @param compareFn Optional function to compare elements.
 * @returns An array containing elements that each are exclusive to `arrA` or `arrB`.
 * @devnote Unlike `lodash.xor` which operates on a pure Set semantic (where duplicates are removed), this method considers the cardinality of elements and retains duplicates accordingly. (Comparable to Apache Commons CollectionUtils)
 */
export function disjunction<T = unknown>( arrA: T[], arrB: T[], compareFn?: BiPredicate<T> ): T[] {
    requireArray( arrA, 'arrA' );
    requireArray( arrB, 'arrB' );
    return difference( arrA, arrB, compareFn ).concat( difference( arrB, arrA, compareFn ) );
}

/**
 * Checks if two arrays contain the same elements with the same frequency, regardless of their order.\
 * If a compare function is provided, it checks for matches based on that function instead of strict equality (`===`).
 * 
 * For example, with a compare function that checks for object equality based on an `id` property, two arrays of objects would be considered equal
 * if they contain the same number of objects with the same `id` values, regardless of their order or other properties.
 * 
 * @param arrA The first array.
 * @param arrB The second array.
 * @param compareFn Optional function to compare elements for equality.
 * @returns `true` if both arrays contain the same elements with the same frequency, `false` otherwise.
 * @example ```ts
 * [1, 2, 2, 3] = [3, 2, 1, 2] // true
 * [1, 2, 3]    = [1, 1, 2, 3] // false
 * 
 * const obj = {};
 * [obj] = [obj] // true
 * [{}]  = [{}]  // false
 * 
 * [{id: 1}, {id: 2}] = [{id: 2}, {id: 1}] // false with default equality, true with compareFn = CompareBy.property('id')
 * ```
 * @devnote Unlike `lodash.isEqual` which operates on a deep equality check, this method only considers the cardinality of elements and disregards their order. (Comparable to Apache Commons CollectionUtils)
 */
export function matches( arrA: any[], arrB: any[], compareFn?: BiPredicate ): boolean {
    const basicEqualityResult = basicEqualityCompare( arrA, arrB );
    if ( basicEqualityResult !== undefined ) {
        return basicEqualityResult;
    }

    if ( compareFn ) {
        // consume the first match for each element in arrA; ensures an exact match, even with duplicate elements
        const remaining = [...arrB];
        return arrA.every( consumeMatch( remaining, compareFn ) );
    }

    // cardinality compare for an exact match using default equality
    return cardinalityCompare( arrA, arrB );
}

/**
 * Checks if two arrays are strictly equal, meaning they contain the same elements in the same order, using strict equality (`===`) or an optional compare function.
 * 
 * This is a more performant check than `equals` when order matters, as it can short-circuit on the first mismatch without needing to build cardinality maps or consume matches.\
 * Should still be faster than `lodash.isEqual` for arrays, as it does not perform deep equality checks on nested structures. (Or more directed through the compare function)
 * @param arrA The first array.
 * @param arrB The second array.
 * @param compareFn Optional function to compare elements for equality. Defaults to strict equality (`===`).
 * @returns `true` if both arrays contain the same elements in the same order, `false` otherwise.
 * @example ```ts
 * [1, 2, 2, 3] = [3, 2, 1, 2] // false
 * [1, 1, 2, 3] = [1, 1, 2, 3] // true
 * 
 * compareFn = CompareBy.property('id'):
 * [{id: 1}, {id: 2}] = [{id: 2}, {id: 1}] // false
 * [{id: 1}, {id: 2}] = [{id: 1}, {id: 2}] // true
 * ```
 */
export function equals( arrA: any[], arrB: any[], compareFn: BiPredicate = CompareBy.equality ): boolean {
    const basicEqualityResult = basicEqualityCompare( arrA, arrB );
    if ( basicEqualityResult !== undefined ) {
        return basicEqualityResult;
    }
    return arrA.length === arrB.length && arrA.every( ( element, index ) => compareFn( element, arrB[index] ) );
}

/** Executes the basic equality comparison between two values, using reference equality and type checking. */
export function basicEqualityCompare( a: any, b: any ): boolean | undefined {
    if ( a == b ) {
        return true;
    }
    if ( !Array.isArray( a ) || !Array.isArray( b ) ) {
        // If one of them is not an array (or both), they are not strictly equal (since they are not the same reference, which was checked above)
        return false;
    }
    return undefined;
}

/**
 * Checks if two arrays contain the same elements with the same frequency, regardless of their order.\
 * For example, `[1, 2, 2, 3]` and `[3, 2, 1, 2]` would be considered equal, while `[1, 2, 3]` and `[1, 1, 2, 3]` would not.
 */
export function cardinalityCompare( arrA: any[], arrB: any[] ): boolean {
    requireArray( arrA, 'arrA' );
    requireArray( arrB, 'arrB' );
    const cardA = getCardinalityMap( arrA );
    const cardB = getCardinalityMap( arrB );
    if ( cardA.size !== cardB.size ) {
        return false;
    }
    for ( const [key, count] of cardA ) {
        if ( cardB.get( key ) !== count ) {
            return false;
        }
    }
    return true;
}

function filterByCardinality<T>( arrA: T[], arrB: T[], keepMatch: boolean ): T[] {
    const card = getCardinalityMap( arrB );
    return arrA.filter( element => {
        const count = card.get( element );
        if ( count && count > 0 ) {
            card.set( element, count - 1 );
            return keepMatch;
        }
        return !keepMatch;
    } );
}

/**
 * Returns a matcher function that checks if an element matches any element in the provided array, using an optional compare function for equality check.\
 * If no compare function is provided, it defaults to using `Array.includes` for comparison.
 */
function matchesAny<T>( array: T[], compareFn?: BiPredicate<T> ) {
    if ( compareFn ) {
        return ( element: T ) => array.some( i => compareFn( element, i ) );
    }
    return ( element: T ) => array.includes( element );
}

/**
 * Returns a predicate that consumes the first matching element from the provided array based on the compare function.\
 * Returns `false` if no match is found, otherwise `true`.\
 * Note: This function mutates the provided array by removing matched elements to accurately reflect remaining elements for duplicates.
 */
function consumeMatch<T>( array: T[], compareFn: BiPredicate<T>, keepAction?: 'keepUnmatched' ) {
    return ( element: T ) => {
        const index = array.findIndex( i => compareFn( element, i ) );
        if ( index !== -1 ) {
            array.splice( index, 1 );
            return keepAction !== 'keepUnmatched';
        }
        return keepAction === 'keepUnmatched';
    };
}

/**
 * Returns a map of the cardinality of each element in the array, i.e. how many times each element occurs.\
 * The keys of the map are the unique elements of the array, and the values are their respective counts.
 */
function getCardinalityMap<T>( array: T[] ): Map<T, number> {
    return array.reduce(
        ( map, item ) => map.set( item, ( map.get( item ) ?? 0 ) + 1 ),
        new Map<T, number>()
    );
}

/**
 * Returns the elements that are in `arrA` but not in `arrB` using strict equality (`===`)
 * and with no regards to cardinality. Duplicates in `arrA` are not removed.\
 * This differs from both lodash.difference (which removes duplicates) and the `difference` method of this class (which takes cardinality into account).
 * @deprecated should be replaced with either the lodash.difference method or the `difference` method of this class, depending on whether cardinality should be considered or not.\
 */
export function getNotContainedElements( arrA: any[], arrB: any[] ) {
    requireArray( arrA, 'arrA' );
    requireArray( arrB, 'arrB' );
    return arrA.filter( element => !arrB.includes( element ) );
}

/**
 * sets all elements of a string array to lower case
 * @return an array of string with all elemnts set to lower case
 */
export function toLowerCase( stringArray: string[] ): string[] {
    return stringArray.map( str => str.toLowerCase() );
}

/**
 * Creates a range of numbers from 0 to the specified length - 1.\
 * Length must be a non-negative integer. If length is 0, an empty array is returned.\
 * ```ts
 * HisArrayUtils.range( 5 ); // returns [0, 1, 2, 3, 4]
 * ```
 */
export function range( length: number ): number[]
/**
 * Creates a range of numbers from start to end.\
 * Start must not be greater than end. Both start and end must be integers, including negative numbers.\
 * ```ts
 * range( 2, 5 ); // returns [2, 3, 4, 5]
 * range( -2, 2 ); // returns [-2, -1, 0, 1, 2]
 * ```
 */
export function range( start: number, end: number ): number[]
export function range( start: number, end?: number ): number[] {
    if ( end === undefined ) {
        if ( start < 0 ) {
            throw new Error( 'Length must be non-negative' );
        }
        if ( start === 0 ) {
            return [];
        }
        end = start - 1;
        start = 0;
    }
    if ( start > end ) {
        throw new Error( 'Start must not be greater than end' );
    }
    const length = end - start + 1;
    return Array.from( { length }, ( _, i ) => start + i );
}

/**
 * Pads an array with its own elements recursively to reach the target length.
 * If the array is empty or already meets/exceeds the target length, it is returned as is.
 * Otherwise, elements are repeated in a cyclic manner until the target length is reached.
 *
 * @param arr The array to pad.
 * @param targetLength The desired length of the array.
 * @returns A new array padded to the target length.
 */
export function padRecursive<T>( arr: T[], targetLength: number ): T[] {
    if ( arr.length >= targetLength || arr.length === 0 ) {
        return arr;
    }
    return Array.from( { length: targetLength }, ( _, i ) => arr[i % arr.length] );
}

/**
 * Switches the elements at the specified indices in the given array.\
 * Modifies the original array and returns it.
 * 
 * If the indices are the same, the array is returned unchanged.
 * @param arr The array in which elements will be switched.
 * @param indexA The index of the first element to switch.
 * @param indexB The index of the second element to switch.
 * @returns The modified array with the elements at indexA and indexB switched.
 * @throws `ValueError` if either index is out of bounds for the array.
 */
export function switchElements<T>( arr: T[], indexA: number, indexB: number ): T[] {
    if ( indexA === indexB ) {
        return arr;
    }
    requireTrue( isInRange( indexA, 0, arr.length - 1, true ), `indexA (${indexA}) is out of bounds for array of length ${arr.length}` );
    requireTrue( isInRange( indexB, 0, arr.length - 1, true ), `indexB (${indexB}) is out of bounds for array of length ${arr.length}` );
    [arr[indexA], arr[indexB]] = [arr[indexB], arr[indexA]];
    return arr;
}

/**
 * Switches the element at the specified index with its neighbor in the given array.\
 * Modifies the original array and returns it.
 * @param arr The array in which elements will be switched.
 * @param index The index of the element to switch with its neighbor.
 * @param direction The direction in which to switch the element ('left' = _towards 0_ or 'right' = _towards the end of the array_).
 * @returns The modified array with the element switched with its neighbor.
 * @throws `ValueError` if the index is out of bounds or the switch cannot be performed in the specified direction.
 */
export function switchNeighbors<T>( arr: T[], index: number, direction: 'left' | 'right' ): T[] {
    requireTrue( isInRange( index, 0, arr.length - 1, true ), `index (${index}) is out of bounds for array of length ${arr.length}` );
    if ( direction === 'left' && index > 0 ) {
        return switchElements( arr, index, index - 1 );
    }
    if ( direction === 'right' && index < arr.length - 1 ) {
        return switchElements( arr, index, index + 1 );
    }
    throw new ValueError( `Cannot switch element at index ${index} to the ${direction} in an array of length ${arr.length}` );
}

/**
 * Bubbles an element from one index to another in the given array, shifting other elements along the way accordingly.\
 * Modifies the original array and returns it.
 * 
 * If the source and target indices are the same, the array is returned unchanged.
 * @example
 * ```ts
 * const arr = [1, 2, 3, 4, 5];
 * bubbleElement(arr, 1, 4); // returns [1, 3, 4, 5, 2]
 * ```
 * **Note:** Instead of _switching_ '2' with '5', it _bubbles_ '2' to the end, shifting '3', '4', and '5' to the left.
 * @param arr The array in which the element will be bubbled.
 * @param idxFrom The index of the element to bubble.
 * @param idxTo The target index to which the element should be bubbled.
 * @returns The modified array with the element bubbled to the target index.
 */
export function bubbleElement<T>( arr: T[], idxFrom: number, idxTo: number ): T[] {
    requireTrue( isInRange( idxFrom, 0, arr.length - 1, true ), `idxFrom (${idxFrom}) is out of bounds for array of length ${arr.length}` );
    requireTrue( isInRange( idxTo, 0, arr.length - 1, true ), `idxTo (${idxTo}) is out of bounds for array of length ${arr.length}` );
    if ( idxFrom === idxTo ) {
        return arr;
    }
    const step = idxFrom < idxTo ? 1 : -1;
    for ( let i = idxFrom; i !== idxTo; i += step ) {
        [arr[i], arr[i + step]] = [arr[i + step], arr[i]];
    }
    return arr;
}
